import React from "react"
import * as projectApi from "../api"
import { useProjectMutations } from "./use-project-mutations"
import { useLazyGetTasksQuery } from "../../tasks/api"
import { useConfirmDialog } from "../../components/ConfirmDialog"
import { Status } from "../../types/common"
import { isCompleted, isNotStarted } from "../../utils/status"
import { TaskViewModel } from "../../tasks/api/task"
import { ProjectViewModel } from "../api/project"
import { useSnackbar } from "notistack"
import { useTranslation } from "react-i18next"
import { DateTimeService } from "../../services/date-time-service"

type Translations = typeof defaultTranslations

export const useUpdateProjectStatus = (project?: ProjectViewModel) => {
  const translations = useTranslations()
  const projectActions = useProjectMutations()
  const [getTasks] = useLazyGetTasksQuery()
  const { enqueueSnackbar } = useSnackbar()
  const [updatedTasks, setUpdatedTasks] = React.useState<TaskViewModel[]>([])
  const [updatedProject, setUpdatedProject] = React.useState<ProjectViewModel>()

  const onConfirm = async (projectId: string, updateData: projectApi.ProjectStatusUpdateData) => {
    const response = await projectActions.updateStatus(projectId, {
      ...updateData,
      updateProjectTasks: true,
    })
    if (response) {
      const { project, tasks } = response
      setUpdatedProject(project)
      setUpdatedTasks(tasks)
    }
  }

  const onCancel = async (projectId: string, updateData: projectApi.ProjectStatusUpdateData) => {
    const response = await projectActions.updateStatus(projectId, {
      ...updateData,
      updateProjectTasks: false,
    })
    if (response) {
      const { project, tasks } = response
      setUpdatedProject(project)
      setUpdatedTasks(tasks)
    }
  }

  const { confirm: confirmChangeAllToCompleted } = useConfirmDialog({
    onConfirm,
    onCancel,
    primaryActionButtonLabel: translations.changeThisProjectAndItsTasksButtonLabel,
    secondaryActionButtonLabel: translations.changeThisProjectOnlyButtonLabel,
    text: translations.completedDialogText,
    title: translations.dialogTitle,
  })

  const { confirm: confirmChangeAllToNotStarted } = useConfirmDialog({
    onConfirm,
    onCancel,
    primaryActionButtonLabel: translations.changeThisProjectAndItsTasksButtonLabel,
    secondaryActionButtonLabel: translations.changeThisProjectOnlyButtonLabel,
    text: translations.notStartedDialogText,
    title: translations.dialogTitle,
  })

  const updateProjectStatus = async (projectId: string, updateData: projectApi.ProjectStatusUpdateData) => {
    const { status, actualEndDate, actualStartDate } = updateData
    const currentStart = updatedProject?.actualStartDate ?? project?.actualStartDate ?? ""
    const currentEnd = updatedProject?.actualEndDate ?? project?.actualEndDate ?? ""
    const now = new Date()

    // Actual start date cannot be in the future
    if (actualStartDate && new Date(actualStartDate) > now) {
      enqueueSnackbar(translations.actualStartDateCannotBeInFuture, { variant: "error" })
      return
    }
    // Actual end date cannot be in the future
    if (actualEndDate && new Date(actualEndDate) > now) {
      enqueueSnackbar(translations.actualEndDateCannotBeInFuture, { variant: "error" })
      return
    }
    // Start date should not be after end date and vice versa validations
    if (actualStartDate && currentEnd) {
      const start = !project?.enableTimeComponent
        ? DateTimeService.toLocalDateOnly(actualStartDate)
        : new Date(actualStartDate)
      const end = !project?.enableTimeComponent ? DateTimeService.toLocalDateOnly(currentEnd) : new Date(currentEnd)
      if (start > end) {
        enqueueSnackbar(translations.actualStartDateCannotBeAfterEndDate, { variant: "error" })
        return
      }
    }
    if (actualEndDate && currentStart) {
      const start = !project?.enableTimeComponent
        ? DateTimeService.toLocalDateOnly(currentStart)
        : new Date(currentStart)
      const end = !project?.enableTimeComponent
        ? DateTimeService.toLocalDateOnly(actualEndDate)
        : new Date(actualEndDate)

      if (end < start) {
        enqueueSnackbar(translations.actualEndDateCannotBeBeforeStartDate, { variant: "error" })
        return
      }
    }
    const hasChangedToCompleted = (status && isCompleted(status)) || Boolean(actualEndDate)
    const hasChangedToNotStarted = (status && isNotStarted(status)) || actualStartDate === ""
    const response = await projectActions.updateStatus(projectId, updateData)
    if (response) {
      const { project } = response
      setUpdatedProject(project)
    }
    const shouldFetchTasks = hasChangedToCompleted || hasChangedToNotStarted
    const tasks = shouldFetchTasks ? await getTasks({ project: projectId }).unwrap() : []

    if (hasChangedToCompleted && shouldConfirmChangeAllToCompleted(projectId, tasks)) {
      return confirmChangeAllToCompleted(projectId, updateData)
    } else if (hasChangedToNotStarted && shouldConfirmChangeAllToNotStarted(projectId, tasks)) {
      return confirmChangeAllToNotStarted(projectId, updateData)
    } else {
      if (response) {
        const { tasks } = response
        setUpdatedTasks(tasks)
      }
    }
  }

  return {
    updateProjectStatus,
    updatedTasks,
    updatedProject,
  }
}

const defaultTranslations = {
  dialogText:
    "This project has subtasks whose current status is not {{status}}. Do you want to change the status of all tasks to {{status}} ?",
  notStartedDialogText:
    "This project has subtasks whose current status is not {{status}}. Do you want to change the status of all tasks to {{status}} ?",
  completedDialogText:
    "This project has subtasks whose current status is not {{status}}. Do you want to change the status of all tasks to {{status}} ?",
  dialogTitle: "Change status of tasks too?",
  changeThisProjectAndItsTasksButtonLabel: "Yes, change the project and its tasks",
  changeThisProjectOnlyButtonLabel: "No, change the project only",
  notStarted: "Not started",
  inProgress: "In progress",
  completed: "Completed",
  rejected: "Rejected",
  actualStartDateCannotBeAfterEndDate: "The actual start date cannot be later than the actual end date.",
  actualEndDateCannotBeBeforeStartDate: "The actual end date cannot be earlier than the actual start date.",
  actualStartDateCannotBeInFuture: "The actual start date cannot be in the future.",
  actualEndDateCannotBeInFuture: "The actual end date cannot be in the future.",
}

const useTranslations = (): Translations => {
  const { t: tProject } = useTranslation("project")
  const notStarted = tProject("projectStatusMenu.notStarted", { defaultValue: defaultTranslations.notStarted })
  const completed = tProject("projectStatusMenu.completed", { defaultValue: defaultTranslations.completed })

  const dialogText = tProject("projectStatusMenu.dialogText", {
    defaultValue: defaultTranslations.dialogText,
    status: "{{status}}",
  })
  const notStartedDialogText = tProject("projectStatusMenu.dialogText", {
    defaultValue: defaultTranslations.notStartedDialogText,
    status: notStarted,
  })
  const completedDialogText = tProject("projectStatusMenu.dialogText", {
    defaultValue: defaultTranslations.completedDialogText,
    status: completed,
  })
  const dialogTitle = tProject("projectStatusMenu.dialogTitle", { defaultValue: defaultTranslations.dialogTitle })
  const changeThisProjectAndItsTasksButtonLabel = tProject(
    "projectStatusMenu.changeThisProjectAndItsTasksButtonLabel",
    {
      defaultValue: defaultTranslations.changeThisProjectAndItsTasksButtonLabel,
    }
  )
  const changeThisProjectOnlyButtonLabel = tProject("projectStatusMenu.changeThisProjectOnlyButtonLabel", {
    defaultValue: defaultTranslations.changeThisProjectOnlyButtonLabel,
  })
  const inProgress = tProject("projectStatusMenu.inProgress", { defaultValue: defaultTranslations.inProgress })
  const rejected = tProject("projectStatusMenu.rejected", { defaultValue: defaultTranslations.rejected })
  const actualStartDateCannotBeAfterEndDate = tProject("projectDetails.actualStartCannotBeAfterEndError", {
    defaultValue: defaultTranslations.actualStartDateCannotBeAfterEndDate,
  })
  const actualEndDateCannotBeBeforeStartDate = tProject("projectDetails.actualEndCannotBeBeforeStartError", {
    defaultValue: defaultTranslations.actualEndDateCannotBeBeforeStartDate,
  })
  const actualStartDateCannotBeInFuture = tProject("projectDetails.actualStartDateCannotBeInFuture", {
    defaultValue: defaultTranslations.actualStartDateCannotBeInFuture,
  })
  const actualEndDateCannotBeInFuture = tProject("projectDetails.actualEndDateCannotBeInFuture", {
    defaultValue: defaultTranslations.actualEndDateCannotBeInFuture,
  })
  return {
    dialogText,
    notStartedDialogText,
    completedDialogText,
    dialogTitle,
    changeThisProjectAndItsTasksButtonLabel,
    changeThisProjectOnlyButtonLabel,
    notStarted,
    inProgress,
    completed,
    rejected,
    actualStartDateCannotBeAfterEndDate,
    actualEndDateCannotBeBeforeStartDate,
    actualStartDateCannotBeInFuture,
    actualEndDateCannotBeInFuture,
  }
}

function shouldConfirmChangeAllToCompleted(
  projectId: string,
  tasks: { status: Status; projectId: string; archived: boolean }[]
) {
  const projectTasks = tasks.filter((t) => t.projectId === projectId)
  const areAllTasksCompleted = projectTasks.every((t) => (!t.archived ? isCompleted(t.status) : true))
  return !areAllTasksCompleted
}

function shouldConfirmChangeAllToNotStarted(
  projectId: string,
  tasks: { status: Status; projectId: string; archived: boolean }[]
) {
  const projectTasks = tasks.filter((t) => t.projectId === projectId)
  const areAllTasksNotStarted = projectTasks.every((t) => (!t.archived ? isNotStarted(t.status) : true))
  return !areAllTasksNotStarted
}
